import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  ButtonText,
  ClearButton,
  OutlineCard,
  SearchInput,
  Table,
  useToast
} from '@morressier/alembic';
import {
  faArrowLeft,
  faDeleteRight,
  faDownload,
  faHashtag,
  faSearch,
  faXmark
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { IActivityLog } from '@publishing-engine/domain/models';
import {
  fetchSubmissionsDashBoardConfig,
  fetchNotificationWebhookEvents,
  fetchNotificationWebhookEvent,
  exportNotificationWebhookEvents,
  IProcessedWebhookEvent,
  ISubmissionSortOption
} from 'src/api/queries';
import { useRetryNotificationMail } from 'src/api/mutations';
import { Overlay } from 'src/components/Overlay';
import TablePagination, {
  DEFAULT_PAGE_SIZE
} from 'src/components/TablePagination';
import { generateActivityCopyText } from 'src/components/Submission/ActivityTab/activityLog';
import RichTextPreview from 'src/components/Submission/GenericFieldComponents/RichText/RichTextPreview';
import TextWithIcon from 'src/components/TextWithIcon';
import FilterDrawer from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Filters/FilterDrawer';
import SortMenu, {
  SortField
} from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Sort/SortMenu';
import SubmissionLoader from 'src/pages/Organization/Workspace/Submissions/SubmissionLoader';
import { removeHrefKeepText, renderRecipient } from 'src/utils/email.utils';
import { useWorkspace } from '../../SubmissionContexts/Users/userHook';
import { EmailRecipientRow } from 'src/components/Submission/ActivityTab/EmailViewModal';
import { formatDateAndTime } from 'src/utils/date';
import { ClampedTooltipText } from 'src/components/shared/ClampedTooltipText';

const getExportFileName = (contentDisposition: string | undefined) => {
  const fileNameMatch = contentDisposition?.match(
    /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i
  );

  return fileNameMatch?.[1]
    ? decodeURIComponent(fileNameMatch[1])
    : 'email-error-logs.csv';
};

const downloadCsv = (
  buffer: ArrayBuffer,
  fileName: string,
  contentType = 'text/csv'
) => {
  const blob = new Blob([buffer], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

type ErrorLogFilterKey =
  | 'eventType'
  | 'reason'
  | 'status'
  | 'submissionContextId';

type ErrorLogFilters = Record<ErrorLogFilterKey, string[]>;

const defaultValues: ErrorLogFilters = {
  eventType: [],
  reason: [],
  status: [],
  submissionContextId: []
};

const getDisplayLabel = (value?: string) => {
  if (!value) return 'NA';

  const label = value
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

  return label.charAt(0).toUpperCase() + label.slice(1);
};

const getEventLabel = (eventType: string) => getDisplayLabel(eventType);

const getCustomArg = (
  event: IProcessedWebhookEvent,
  key: string
): string | undefined => {
  const value = event.metadata?.customArgs?.[key];
  return typeof value === 'string' ? value : undefined;
};

const getTrackerId = (event: IProcessedWebhookEvent) =>
  event.trackerId ??
  event.emailDetail?.trackerId ??
  getCustomArg(event, 'trackerId');

const getSubmissionId = (event: IProcessedWebhookEvent) =>
  event.emailDetail?.submissionId ?? getCustomArg(event, 'submissionId');

const getSubmissionContextId = (event: IProcessedWebhookEvent) =>
  event.emailDetail?.submissionContextId ??
  getCustomArg(event, 'submissionContextId');

const getEmailActivityTitle = (event: IProcessedWebhookEvent): ReactNode => {
  const activityType = event.emailDetail?.type;
  const fallbackTitle = getDisplayLabel(event.emailDetail?.subType);

  if (!activityType) return fallbackTitle;

  const activityTitle = generateActivityCopyText({
    type: activityType,
    submissionId: event.emailDetail?.submissionId ?? '',
    submissionContextId: '',
    date: event.timestamp,
    userId: '',
    userFirstName: '',
    userLastName: '',
    manuscriptId: event.emailDetail?.manuscriptId,
    action: event.emailDetail?.subType
  } as IActivityLog).title;

  return typeof activityTitle === 'string'
    ? getDisplayLabel(activityTitle)
    : activityTitle || fallbackTitle;
};

const DetailField = ({
  label,
  children
}: {
  label: string;
  children: ReactNode;
}) => (
  <Flex direction="column" gap={1} minWidth="0">
    <Text textStyle="headingItem">{label}</Text>
    <Text textStyle="textBody" whiteSpace="pre-wrap" wordBreak="break-word">
      {children}
    </Text>
  </Flex>
);

const DeliveryDetailsTable = ({ logs }: { logs: IProcessedWebhookEvent[] }) => (
  <Box width="100%" overflowX="auto">
    <Box minWidth="860px">
      <Table>
        <Table.HeaderRow gap={4}>
          <Table.HeaderCell flex="1.1">Sequence ID</Table.HeaderCell>
          <Table.HeaderCell flex="1.2">Error Type</Table.HeaderCell>
          <Table.HeaderCell flex="1">Reason</Table.HeaderCell>
          <Table.HeaderCell flex="1.4">Event Time</Table.HeaderCell>
        </Table.HeaderRow>
        <Table.Body>
          {logs.map((item, index) => (
            <Table.Row
              key={item._id ?? `${item.sequenceId ?? 'row'}-${index}`}
              gap={4}
              _last={{ borderBottom: 'none' }}
            >
              <Table.RowCell
                flex="1.1"
                paddingY={3}
                minWidth="0"
                overflow="hidden"
              >
                <Text
                  textStyle="textBody"
                  whiteSpace="normal"
                  wordBreak="break-word"
                  overflowWrap="anywhere"
                >
                  {item.sequenceId ?? '--'}
                </Text>
              </Table.RowCell>
              <Table.RowCell flex="1.2" paddingY={3}>
                <Text textStyle="textBody">
                  {getEventLabel(item.eventType)}
                </Text>
              </Table.RowCell>
              <Table.RowCell
                flex="1"
                paddingY={3}
                minWidth="0"
                overflow="hidden"
              >
                <Text
                  textStyle="textBody"
                  whiteSpace="normal"
                  wordBreak="break-word"
                  overflowWrap="anywhere"
                >
                  {String(item.eventData?.reason ?? '--')}
                </Text>
              </Table.RowCell>
              <Table.RowCell flex="1.4" paddingY={3}>
                <Text textStyle="textBody">
                  {item.timestamp
                    ? formatDateAndTime(new Date(item.timestamp))
                    : '--'}
                </Text>
              </Table.RowCell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Box>
  </Box>
);

const EmailLogOverlay = ({
  log,
  onClose,
  onRetry,
  isRetrying
}: {
  log: IProcessedWebhookEvent;
  onClose: () => void;
  onRetry: (log: IProcessedWebhookEvent) => void;
  isRetrying: boolean;
}) => {
  const { data, isLoading } = useQuery({
    queryKey: ['notification-webhook-event', log._id],
    queryFn: () => fetchNotificationWebhookEvent(log._id),
    enabled: !!log._id
  });
  const webhook = data?.webhook ?? log;
  const deliveryLogs = data?.webhook
    ? [data.webhook, ...(data.sisterWebhooks ?? [])]
    : [log];
  const emailDetails = data?.emailDetails;
  const recipients = emailDetails?.recipients;
  const subject = emailDetails?.subject ?? '--';
  const body = emailDetails?.body ?? '';
  const manuscriptId = data?.manuscriptId;
  const trackerId = webhook.trackerId ?? getTrackerId(webhook);

  return (
    <Overlay>
      <Flex
        minHeight="100%"
        direction="column"
        backgroundColor="surfaceSecondaryFill"
      >
        <Flex justifyContent="space-between" alignItems="center" p={6}>
          <ButtonText
            variant="secondary"
            onClick={onClose}
            iconLeft={<FontAwesomeIcon icon={faArrowLeft} />}
          >
            Back to Email Error Logs
          </ButtonText>
        </Flex>

        <Box flex={1} overflowY="auto" px={{ base: 4, md: 6 }} pb={6}>
          {isLoading ? (
            <SubmissionLoader
              title="Loading Email Details"
              subTitle="We are retrieving the email log details."
              bgColor="neutralSecondaryFill"
            />
          ) : (
            <OutlineCard gap={6} maxWidth="980px" mx="auto" p={4}>
              <Flex direction="column" gap={2}>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  gap={4}
                >
                  <Text textStyle="headingSection">
                    {getEventLabel(webhook.eventType)} - {webhook.email ?? '--'}
                  </Text>
                  <Button
                    disabled={!trackerId || isRetrying || webhook?.isRetryDone}
                    isLoading={isRetrying}
                    loadingText="Retrying..."
                    onClick={() => onRetry(webhook)}
                  >
                    {webhook?.isRetryDone ? 'Retried' : 'Retry'}
                  </Button>
                </Flex>

                <Flex gap={4} wrap="wrap">
                  {manuscriptId && (
                    <TextWithIcon icon={faHashtag} text={`${manuscriptId}`} />
                  )}
                </Flex>
              </Flex>
              <Box as="hr" borderColor="borderPrimary" width="100%" mt={2} />
              <Box backgroundColor="white" p={6} borderRadius="small">
                <Flex direction="column" gap={5}>
                  <Text textStyle="headingItem">Delivery Details</Text>
                  <DeliveryDetailsTable logs={deliveryLogs} />
                </Flex>
              </Box>
              <Box as="hr" borderColor="borderPrimary" width="100%" />
              <Box backgroundColor="white" p={6} borderRadius="small">
                <Flex direction="column" gap={5}>
                  <Text textStyle="headingItem">Email Content</Text>
                  <Flex direction="column" gap={1}>
                    <EmailRecipientRow label="To">
                      {renderRecipient(recipients?.to) || webhook.email || '--'}
                    </EmailRecipientRow>
                    <EmailRecipientRow label="CC">
                      {renderRecipient(recipients?.cc) || '--'}
                    </EmailRecipientRow>
                    <EmailRecipientRow label="BCC">
                      {renderRecipient(recipients?.bcc) || '--'}
                    </EmailRecipientRow>
                  </Flex>
                  <DetailField label="Subject">{subject}</DetailField>
                  <Flex direction="column" gap={2}>
                    <Text textStyle="headingItem">Body Text</Text>
                    {body ? (
                      <RichTextPreview value={removeHrefKeepText(body) ?? ''} />
                    ) : (
                      <Text textStyle="textBody">--</Text>
                    )}
                  </Flex>
                </Flex>
              </Box>
            </OutlineCard>
          )}
        </Box>
      </Flex>
    </Overlay>
  );
};

const ErrorsLogTable = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchValue, setSearchValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState<ISubmissionSortOption | null>(null);
  const [selectedLog, setSelectedLog] = useState<IProcessedWebhookEvent | null>(
    null
  );
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});
  const { watch, setValue, reset } = useForm<ErrorLogFilters>({
    defaultValues
  });
  const filters = watch();
  const {
    workspaceId,
    workspaceShortname,
    submissionContextId: fallbackSubmissionContextId
  } = useWorkspace();
  const { retryNotificationMailAsync, isLoading: isRetryingMail } =
    useRetryNotificationMail();

  const view = 'bounce-email-log';
  const { data: dashBoardConfigs } = useQuery({
    queryKey: ['submission-dashboard-config', { workspaceId, view }],
    queryFn: () => fetchSubmissionsDashBoardConfig({ workspaceId, view }),
    enabled: !!workspaceId
  });

  const filterGroups = useMemo(
    () =>
      dashBoardConfigs?.filters?.map((filter: any) => ({
        ...filter,
        items: filter.items ?? []
      })) ?? [],
    [dashBoardConfigs?.filters]
  );

  const trimmedSearchQuery = searchQuery.trim();
  const selectedEventTypes = filters.eventType ?? [];
  const eventTypesKey = selectedEventTypes.join('|');
  const selectedReasons = filters.reason ?? [];
  const selectedStatuses = filters.status ?? [];
  const selectedSubmissionContextIds = filters.submissionContextId ?? [];
  const reasonsKey = selectedReasons.join('|');
  const statusesKey = selectedStatuses.join('|');
  const submissionContextIdsKey = selectedSubmissionContextIds.join('|');
  const emailSearch =
    trimmedSearchQuery.includes('@') &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedSearchQuery)
      ? trimmedSearchQuery
      : undefined;
  const apiSearch = trimmedSearchQuery || undefined;

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'notification-webhook-events',
      {
        page,
        limit: pageSize,
        email: emailSearch,
        search: apiSearch,
        eventTypes: eventTypesKey,
        reason: reasonsKey,
        status: statusesKey,
        submissionContextId: submissionContextIdsKey,
        sort
      }
    ],
    queryFn: () =>
      fetchNotificationWebhookEvents({
        page,
        limit: pageSize,
        email: emailSearch,
        search: apiSearch,
        eventTypes: selectedEventTypes.length ? selectedEventTypes : undefined,
        reason: selectedReasons.length ? selectedReasons : undefined,
        status: selectedStatuses.length ? selectedStatuses : undefined,
        submissionContextId: selectedSubmissionContextIds.length
          ? selectedSubmissionContextIds
          : undefined,
        sort: sort ?? undefined
      })
  });

  const logs = data?.items ?? [];

  useEffect(() => {
    if (!selectedLog) return;

    const updatedLog = logs.find(item => item._id === selectedLog._id);

    if (updatedLog && updatedLog !== selectedLog) {
      setSelectedLog(updatedLog);
    }
  }, [logs, selectedLog]);

  const totalItems = data?.total ?? 0;
  const startIndex = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, totalItems);

  const hasFilters =
    !!searchQuery ||
    !!filters.eventType.length ||
    !!filters.reason.length ||
    !!filters.status.length ||
    !!filters.submissionContextId.length;

  const handleFiltersSubmit = (
    newSelectedFilters: Record<string, string[]>
  ) => {
    Object.entries(newSelectedFilters).forEach(([key, values]) => {
      setValue(key as keyof ErrorLogFilters, [...values] as never);
    });
    setPage(1);
  };

  const removeSingleFilter = (
    filterToRemove: string,
    fieldName: ErrorLogFilterKey
  ) => {
    const updatedFilter = filters[fieldName].filter(
      currentFilter => currentFilter !== filterToRemove
    );

    setValue(fieldName, updatedFilter);
    setSelectedFilters(prev => ({
      ...prev,
      [fieldName]: updatedFilter
    }));
    setPage(1);
  };

  const clearFilters = () => {
    reset(defaultValues);
    setSelectedFilters({});
    setSearchValue('');
    setSearchQuery('');
    setPage(1);
  };

  const handleSortApply = (selected: SortField | null) => {
    setSort(selected);
    setPage(1);
  };

  const handleSortReset = () => {
    setSort(null);
    setPage(1);
  };

  const exportLogsMutation = useMutation({
    mutationFn: () =>
      exportNotificationWebhookEvents({
        email: emailSearch,
        search: apiSearch,
        eventTypes: selectedEventTypes.length ? selectedEventTypes : undefined,
        reason: selectedReasons.length ? selectedReasons : undefined,
        status: selectedStatuses.length ? selectedStatuses : undefined,
        submissionContextId: selectedSubmissionContextIds.length
          ? selectedSubmissionContextIds
          : undefined,
        sort: sort ?? undefined
      }),
    onSuccess: response => {
      const contentType = String(
        response.headers?.['content-type'] ?? 'text/csv'
      );
      const fileName = getExportFileName(
        response.headers?.['content-disposition']
      );

      downloadCsv(response.data, fileName, contentType);
      toast({
        variant: 'success',
        title: 'Email error logs exported',
        status: 'success'
      });
    },
    onError: () => {
      toast({
        variant: 'error',
        title: 'Failed to export email error logs',
        description: 'Please try again later.',
        status: 'error'
      });
    }
  });

  const handleRetryMail = async (log: IProcessedWebhookEvent) => {
    const trackerId = getTrackerId(log);

    if (!trackerId) {
      toast({
        variant: 'error',
        title: 'Unable to retry email',
        description: 'Tracker ID is missing for this email.',
        status: 'error'
      });
      return;
    }

    try {
      await retryNotificationMailAsync({ trackerId, email: log.email });

      toast({
        variant: 'success',
        title: 'Email retry triggered',
        status: 'success'
      });
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Failed to retry email',
        description: 'Please try again later.',
        status: 'error'
      });
    }
  };

  const handleOpenSubmission = (log: IProcessedWebhookEvent) => {
    const submissionId = getSubmissionId(log);
    const submissionContextId =
      getSubmissionContextId(log) || fallbackSubmissionContextId;

    if (!workspaceShortname || !submissionId || !submissionContextId) return;

    navigate(
      `/app/workspace/${workspaceShortname}/j/${submissionContextId}/submissions?selectedItem=${submissionId}&activeTab=overview`,
      {
        state: { from: location.pathname + location.search }
      }
    );
  };

  return (
    <Flex direction="column" gap={4} width="100%" mt={3}>
      <Flex
        gap={4}
        alignItems={{ base: 'stretch', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        width="100%"
      >
        <Flex gap={2} alignItems="center" flex="1">
          <SearchInput
            placeholder="Search by Recipient Email, Subject, Error Type or Manuscript ID"
            iconLeft={<FontAwesomeIcon icon={faSearch} />}
            width="100%"
            name="errorLogSearch"
            value={searchValue}
            enterKeyHint="search"
            onChange={e => setSearchValue(e.currentTarget.value)}
            onInputClear={() => {
              setSearchValue('');
              setSearchQuery('');
              setPage(1);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setSearchQuery(searchValue.trim());
                setPage(1);
              }
            }}
            onKeyUp={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setSearchQuery(searchValue.trim());
                setPage(1);
              }
            }}
          />
          <Box display={{ base: 'block', lg: 'none' }} flexShrink={0}>
            <Button
              variant="primary"
              aria-label="Search email bounces"
              onClick={() => {
                setSearchQuery(searchValue.trim());
                setPage(1);
              }}
            >
              <FontAwesomeIcon icon={faSearch} />
            </Button>
          </Box>
        </Flex>
        <FilterDrawer
          filterGroups={filterGroups}
          watch={watch as any}
          onSubmit={handleFiltersSubmit}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
        />
        {dashBoardConfigs?.sortOptions && (
          <SortMenu
            appliedSort={sort}
            options={dashBoardConfigs.sortOptions}
            onApply={handleSortApply}
            onReset={handleSortReset}
          />
        )}
        <Button
          variant="secondary"
          onClick={() => exportLogsMutation.mutate()}
          leftIcon={<FontAwesomeIcon icon={faDownload} />}
          isLoading={exportLogsMutation.isPending}
        >
          Export
        </Button>
      </Flex>

      <Flex alignItems="center" gap={2} wrap="wrap">
        {isLoading ? (
          <Text textStyle="textBody">Searching for matches...</Text>
        ) : (
          <Text textStyle="textBody">
            {totalItems > 0 ? (
              <>
                Showing <strong>{startIndex}</strong> -{' '}
                <strong>{endIndex}</strong> of <strong>{totalItems}</strong>{' '}
                emails failed to be sent.
              </>
            ) : (
              <>
                Showing <strong>0</strong> emails failed to be sent.
              </>
            )}
          </Text>
        )}

        {hasFilters && (
          <ButtonText
            onClick={clearFilters}
            leftIcon={<FontAwesomeIcon icon={faDeleteRight} />}
            variant="secondary"
          >
            Clear filters
          </ButtonText>
        )}
      </Flex>

      {Object.entries(filters).some(([, value]) => value.length > 0) && (
        <Flex alignItems="center" gap={2} wrap="wrap">
          {filterGroups.map(filterConfig => {
            const filterKey = filterConfig.key as ErrorLogFilterKey;
            const filterValues = filters[filterKey] ?? [];

            if (!filterValues.length) return null;

            return filterValues.map(value => {
              const label =
                filterConfig.items.find(
                  (item: { value: string; name: string }) =>
                    item.value === value
                )?.name ?? value;

              return (
                <Badge
                  key={`${filterKey}-${value}`}
                  size="base"
                  variant="interactive"
                >
                  <Flex alignItems="center" gap={2}>
                    {label}
                    <FontAwesomeIcon
                      icon={faXmark}
                      cursor="pointer"
                      onClick={() => removeSingleFilter(value, filterKey)}
                    />
                  </Flex>
                </Badge>
              );
            });
          })}
        </Flex>
      )}

      {isLoading ? (
        <SubmissionLoader
          title="Loading Email Bounces"
          subTitle="We are retrieving email bounce logs. This should only take a moment."
          bgColor="neutralSecondaryFill"
        />
      ) : isError ? (
        <SubmissionLoader
          title="Failed to Load Errors Log"
          subTitle="There was an error loading webhook events. Please try again later."
          bgColor="errorPrimaryFill"
        />
      ) : logs.length === 0 ? (
        <SubmissionLoader
          title="No Email Bounces Found"
          subTitle="There are no webhook event logs matching your current filters."
          bgColor="neutralSecondaryFill"
        />
      ) : (
        <Box width="100%" overflowX="auto">
          <Box minWidth="1160px">
            <Table>
              <Table.HeaderRow gap={2}>
                <Table.HeaderCell flex="1.1">Sequence ID</Table.HeaderCell>
                <Table.HeaderCell flex="1.8">Recipient Email</Table.HeaderCell>
                <Table.HeaderCell flex="1.8">Subject</Table.HeaderCell>
                <Table.HeaderCell flex="1.6">Manuscript ID</Table.HeaderCell>
                <Table.HeaderCell flex="1.7">Email Event</Table.HeaderCell>
                <Table.HeaderCell flex="1.5">Error Type</Table.HeaderCell>
                <Table.HeaderCell flex="1.4">Event Time</Table.HeaderCell>
              </Table.HeaderRow>
              <Table.Body>
                {logs.map(log => {
                  const ManuscriptID =
                    log.emailDetail?.manuscriptId ??
                    getCustomArg(log, 'manuscriptId') ??
                    '--';
                  const submissionId = getSubmissionId(log);
                  const submissionContextId =
                    getSubmissionContextId(log) || fallbackSubmissionContextId;
                  const manuscriptIdLabel =
                    ManuscriptID !== '' ? ManuscriptID : 'NA';
                  const canOpenSubmission =
                    !!workspaceShortname &&
                    !!submissionId &&
                    !!submissionContextId &&
                    manuscriptIdLabel !== 'NA' &&
                    manuscriptIdLabel !== '--';
                  const title = getEmailActivityTitle(log);
                  const subject = log.emailDetail?.subject ?? '--';
                  const formattedDate = log.timestamp
                    ? formatDateAndTime(new Date(log.timestamp))
                    : '--';

                  return (
                    <Table.Row
                      key={log._id}
                      gap={2}
                      _last={{ borderBottom: 'none' }}
                    >
                      <Table.RowCell
                        flex="1.1"
                        paddingY={3}
                        minWidth="0"
                        overflow="hidden"
                      >
                        <Text
                          textStyle="textBody"
                          textAlign="left"
                          whiteSpace="normal"
                          wordBreak="break-word"
                          overflowWrap="anywhere"
                        >
                          {log.sequenceId ?? '--'}
                        </Text>
                      </Table.RowCell>
                      <Table.RowCell
                        flex="1.8"
                        paddingY={3}
                        minWidth="0"
                        overflow="hidden"
                      >
                        <ClearButton
                          ariaLabel={`Open webhook event ${log.sg_event_id}`}
                          justifyContent="flex-start"
                          height="auto"
                          width="100%"
                          minWidth="0"
                          onClick={() => setSelectedLog(log)}
                        >
                          <Text
                            textStyle="headingItem"
                            color="actionPrimaryFill"
                            textAlign="left"
                            whiteSpace="normal"
                            wordBreak="break-word"
                            overflowWrap="anywhere"
                          >
                            {log.email}
                          </Text>
                        </ClearButton>
                      </Table.RowCell>
                      <Table.RowCell
                        flex="1.8"
                        paddingY={3}
                        minWidth="0"
                        overflow="hidden"
                      >
                        <ClampedTooltipText value={subject} />
                      </Table.RowCell>
                      <Table.RowCell
                        flex="1.6"
                        paddingY={3}
                        minWidth="0"
                        overflow="hidden"
                      >
                        {canOpenSubmission ? (
                          <ClearButton
                            ariaLabel={`Open submission ${manuscriptIdLabel}`}
                            justifyContent="flex-start"
                            height="auto"
                            width="100%"
                            minWidth="0"
                            onClick={() => handleOpenSubmission(log)}
                          >
                            <Text
                              textStyle="headingItem"
                              color="actionPrimaryFill"
                              textAlign="left"
                              whiteSpace="normal"
                              wordBreak="break-word"
                              overflowWrap="anywhere"
                            >
                              {manuscriptIdLabel}
                            </Text>
                          </ClearButton>
                        ) : (
                          <Text textStyle="textBody">{manuscriptIdLabel}</Text>
                        )}
                      </Table.RowCell>
                      <Table.RowCell
                        flex="1.7"
                        paddingY={3}
                        minWidth="0"
                        overflow="hidden"
                      >
                        <Flex
                          direction="column"
                          alignItems="flex-start"
                          gap={1}
                          minWidth="0"
                          width="100%"
                        >
                          <Text
                            textStyle="textBody"
                            textAlign="left"
                            whiteSpace="normal"
                            wordBreak="break-word"
                            overflowWrap="anywhere"
                          >
                            {title}
                          </Text>
                        </Flex>
                      </Table.RowCell>
                      <Table.RowCell
                        flex="1.5"
                        paddingY={3}
                        minWidth="0"
                        overflow="hidden"
                      >
                        <Flex
                          direction="column"
                          alignItems="flex-start"
                          gap={1}
                          minWidth="0"
                          width="100%"
                        >
                          <Text
                            textStyle="textBody"
                            textAlign="left"
                            whiteSpace="normal"
                            wordBreak="break-word"
                            overflowWrap="anywhere"
                          >
                            {getEventLabel(log.eventType)}
                          </Text>
                        </Flex>
                      </Table.RowCell>
                      <Table.RowCell flex="1.4" paddingY={3}>
                        {formattedDate}
                      </Table.RowCell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </Box>
        </Box>
      )}

      <TablePagination
        ariaLabel="Email errors log pagination"
        currentPage={page}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      {selectedLog && (
        <EmailLogOverlay
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
          onRetry={handleRetryMail}
          isRetrying={isRetryingMail}
        />
      )}
    </Flex>
  );
};

export default ErrorsLogTable;
